// Tiny dependency-free SVG charts. No external libraries, no CDN — keeps the
// bundle self-contained and Cloudflare-CSP-friendly. Theme via CSS variables.

const NS = "http://www.w3.org/2000/svg";

function el(tag: string, attrs: Record<string, string | number>): SVGElement {
  const e = document.createElementNS(NS, tag);
  for (const [k, v] of Object.entries(attrs)) e.setAttribute(k, String(v));
  return e;
}

export interface LineSeries {
  points: { period: string; value: number }[];
  color: string;
  label: string;
  axis?: "left" | "right";
  dashed?: boolean; // e.g. a provisional (still-filling) period
}

// A shaded horizontal reference band on the left axis (value space), e.g. the
// "favourable" / "stretched" zones behind an entry-score line so the reader can
// see at a glance where the line sits versus the decision thresholds.
export interface Band {
  from: number;
  to: number;
  color: string; // a CSS color / var(); rendered at low opacity
  label?: string;
}

export interface LineChartOpts {
  height?: number;
  /** Force the left-axis value domain (e.g. 0..100 for a score). */
  yDomain?: { min: number; max: number };
  /** Shaded reference bands on the left axis. */
  bands?: Band[];
  /** Soft area fill under each line (uses the series colour). */
  fill?: boolean;
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
// "2024-03" / "2024-03-01" → "Mar '24"; anything else passes through.
function shortPeriod(s: string): string {
  const m = /^(\d{4})-(\d{2})/.exec(s);
  if (!m) return s;
  const mon = MONTHS[Number(m[2]) - 1] ?? m[2];
  return `${mon} '${m[1].slice(2)}`;
}

/**
 * Multi-series line chart with two optional y-axes and a light grid.
 * Second arg accepts a plain height (legacy) or an options object.
 */
export function lineChart(series: LineSeries[], opts: number | LineChartOpts = {}): SVGElement {
  const o: LineChartOpts = typeof opts === "number" ? { height: opts } : opts;
  const W = 720;
  const H = o.height ?? 240;
  // Roomier gutters + larger type so the chart stays legible when a phone
  // scales this fixed viewBox down to ~340px wide.
  // Gutters wide enough for "S$1.2M"-style labels at the larger phone tick size.
  const pad = { top: 18, right: 64, bottom: 44, left: 92 };
  const svg = el("svg", { viewBox: `0 0 ${W} ${H}`, class: "chart", role: "img",
    "aria-label": chartSummary(series) });

  const all = series.flatMap((s) => s.points);
  if (all.length === 0) {
    svg.appendChild(el("text", { x: W / 2, y: H / 2, "text-anchor": "middle", class: "chart-empty" }))
      .textContent = "No data yet";
    return svg;
  }

  const periods = Array.from(new Set(all.map((p) => p.period))).sort();
  const xOf = (period: string) => {
    const i = periods.indexOf(period);
    return pad.left + (i / Math.max(1, periods.length - 1)) * (W - pad.left - pad.right);
  };

  const axes = { left: series.filter((s) => s.axis !== "right"), right: series.filter((s) => s.axis === "right") };
  const rangeFor = (list: LineSeries[]) => {
    const vs = list.flatMap((s) => s.points.map((p) => p.value));
    if (vs.length === 0) return { min: 0, max: 1 };
    const min = Math.min(...vs);
    const max = Math.max(...vs);
    const pad10 = (max - min) * 0.1 || max * 0.1 || 1;
    return { min: min - pad10, max: max + pad10 };
  };
  const lRange = o.yDomain ?? rangeFor(axes.left);
  const rRange = rangeFor(axes.right);
  const yOf = (v: number, side: "left" | "right") => {
    const r = side === "right" ? rRange : lRange;
    const t = (v - r.min) / (r.max - r.min || 1);
    return pad.top + (1 - t) * (H - pad.top - pad.bottom);
  };
  const plotTop = pad.top;
  const plotBot = H - pad.bottom;
  const clampY = (y: number) => Math.max(plotTop, Math.min(plotBot, y));

  // reference bands (behind the grid + lines)
  for (const b of o.bands ?? []) {
    const y1 = clampY(yOf(b.to, "left"));
    const y2 = clampY(yOf(b.from, "left"));
    const top = Math.min(y1, y2);
    const hgt = Math.abs(y2 - y1);
    if (hgt <= 0) continue;
    svg.appendChild(el("rect", {
      x: pad.left, y: top, width: W - pad.left - pad.right, height: hgt,
      fill: b.color, opacity: 0.1,
    }));
    if (b.label) {
      const t = el("text", { x: W - pad.right - 4, y: top + 13, "text-anchor": "end", class: "chart-band-label", fill: b.color });
      t.textContent = b.label;
      svg.appendChild(t);
    }
  }

  // grid + left axis labels
  for (let g = 0; g <= 4; g++) {
    const y = pad.top + (g / 4) * (H - pad.top - pad.bottom);
    svg.appendChild(el("line", { x1: pad.left, y1: y, x2: W - pad.right, y2: y, class: "chart-grid" }));
    const val = lRange.max - (g / 4) * (lRange.max - lRange.min);
    const lbl = el("text", { x: pad.left - 8, y: y + 4, "text-anchor": "end", class: "chart-tick" });
    lbl.textContent = compact(val);
    svg.appendChild(lbl);
  }

  // x labels (first, middle, last) — month-formatted, not raw YYYY-MM.
  [0, Math.floor(periods.length / 2), periods.length - 1].forEach((i, idx, arr) => {
    if (i < 0 || i >= periods.length) return;
    if (idx > 0 && i === arr[idx - 1]) return; // dedupe when very few points
    const anchor = idx === 0 ? "start" : idx === arr.length - 1 ? "end" : "middle";
    const t = el("text", { x: xOf(periods[i]), y: H - 12, "text-anchor": anchor, class: "chart-tick" });
    t.textContent = shortPeriod(periods[i]);
    svg.appendChild(t);
  });

  // series area fills + paths
  for (const s of series) {
    const side = s.axis ?? "left";
    const pts = [...s.points].sort((a, b) => (a.period < b.period ? -1 : 1));
    const line = pts
      .map((p, i) => `${i === 0 ? "M" : "L"} ${xOf(p.period).toFixed(1)} ${yOf(p.value, side).toFixed(1)}`)
      .join(" ");
    if (o.fill && pts.length > 1 && !s.dashed) {
      const base = plotBot;
      const area = `${line} L ${xOf(pts[pts.length - 1].period).toFixed(1)} ${base.toFixed(1)} L ${xOf(pts[0].period).toFixed(1)} ${base.toFixed(1)} Z`;
      svg.appendChild(el("path", { d: area, fill: s.color, opacity: 0.08, stroke: "none" }));
    }
    svg.appendChild(el("path", { d: line, fill: "none", stroke: s.color, "stroke-width": 2.5, "stroke-linejoin": "round", ...(s.dashed ? { "stroke-dasharray": "5 4", opacity: 0.7 } : {}) }));
    const last = pts[pts.length - 1];
    svg.appendChild(el("circle", { cx: xOf(last.period), cy: yOf(last.value, side), r: 3.5, fill: s.color }));
  }

  return svg;
}

/** Entry-score gauge (0..100). Higher = better time to enter. */
export function scoreGauge(score: number, label: string): SVGElement {
  const W = 200;
  const H = 120;
  const cx = W / 2;
  const cy = 100;
  const r = 78;
  const clampedScore = Math.round(Math.max(0, Math.min(100, score)));
  const svg = el("svg", { viewBox: `0 0 ${W} ${H}`, class: "gauge", role: "img",
    "aria-label": `Timing score ${clampedScore} out of 100 — ${label}` });

  const arc = (from: number, to: number, color: string, width: number) => {
    const a0 = Math.PI * (1 + from / 100);
    const a1 = Math.PI * (1 + to / 100);
    const x0 = cx + r * Math.cos(a0);
    const y0 = cy + r * Math.sin(a0);
    const x1 = cx + r * Math.cos(a1);
    const y1 = cy + r * Math.sin(a1);
    // The gauge spans at most 180°, so the value arc is always the minor arc —
    // the large-arc flag must stay 0 or arcs above 50 sweep the long way round.
    const large = 0;
    svg.appendChild(
      el("path", {
        d: `M ${x0} ${y0} A ${r} ${r} 0 ${large} 1 ${x1} ${y1}`,
        fill: "none",
        stroke: color,
        "stroke-width": width,
        "stroke-linecap": "round",
      }),
    );
  };

  arc(0, 100, "var(--track)", 12);
  const clamped = Math.max(0, Math.min(100, score));
  const color = clamped >= 62 ? "var(--good)" : clamped <= 40 ? "var(--bad)" : "var(--neutral)";
  arc(0, clamped, color, 12);

  const num = el("text", { x: cx, y: cy - 8, "text-anchor": "middle", class: "gauge-num" });
  num.textContent = String(Math.round(clamped));
  svg.appendChild(num);
  const lab = el("text", { x: cx, y: cy + 12, "text-anchor": "middle", class: "gauge-label" });
  lab.textContent = label;
  svg.appendChild(lab);
  return svg;
}

// Screen-reader summary of a line chart: each series' first and latest point,
// so the trend is available without seeing the SVG (WCAG 1.1.1).
function chartSummary(series: LineSeries[]): string {
  const parts = series
    .filter((s) => s.points.length > 0)
    .map((s) => {
      const a = s.points[0];
      const b = s.points[s.points.length - 1];
      return `${s.label}: ${compact(a.value)} in ${shortPeriod(a.period)} to ${compact(b.value)} in ${shortPeriod(b.period)}`;
    });
  return parts.length ? `Line chart. ${parts.join("; ")}.` : "Line chart, no data yet.";
}

function compact(v: number): string {
  if (Math.abs(v) >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M`;
  if (Math.abs(v) >= 1_000) return `${Math.round(v / 1_000)}k`;
  return String(Math.round(v));
}
